/**
 * Implements infinite scrolling functionality for a web component.
 *  This component loads additional content automatically as the user scrolls to the bottom of the page.
 *  It requires an element with class `.js-pagination__next-button` for fetching the next set of items.
 *
 * Usage:
 *  {%- paginate blog.articles by section.settings.items_per_page -%}
 *    <infinite-scroll data-infinite-scroll-enabled="{% if settings.pagination_style == 'infinite' %}true{% else %}false{% endif %}">
 *      <div class="js-pagination__item">One</div>
 *      <div class="js-pagination__item">Two</div>
 *      <div class="js-pagination__item">Three</div>
 *
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate %}
 *      {%- endif -%}
 *    </infinite-scroll>
 *  {%- endpaginate -%}
 */
if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    // Cleans up resources when the element is removed from the DOM
    disconnectedCallback() {
      this.destroy();
    }

    init() {
      // Elements involved in pagination
      this.paginationContainer = this.querySelector('.js-pagination');
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      this.nextButton = this.querySelector('.js-pagination__next-button');

      // Guard clause to ensure necessary elements exist
      if (!this.paginationContainer || !this.paginationItems.length || !this.nextButton?.hasAttribute('href')) {
        return;
      }

      this.normalizeProductBadges(this.paginationItems);
      this.bindListeners();
    }

    // Removes event listeners and stops any ongoing intervals or observers
    destroy() {
      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.intersectionObserver?.disconnect();
    }

    // Handles the "Next" button click to fetch the next set of items
    onNextButtonClick(event) {
      event.preventDefault();
      this.fetchNextItems();
    }

    getErrorMessage() {
      let errorMessage = this.paginationContainer?.querySelector('.js-pagination__error');

      if (!errorMessage && this.paginationContainer) {
        errorMessage = document.createElement('p');
        errorMessage.className = 'pagination__error js-pagination__error';
        errorMessage.setAttribute('role', 'alert');
        errorMessage.textContent = 'Nie udało się załadować produktów. Spróbuj ponownie.';
        errorMessage.hidden = true;
        errorMessage.style.margin = '1.2rem 0 0';
        errorMessage.style.color = '#b42318';
        errorMessage.style.textAlign = 'center';
        this.paginationContainer.appendChild(errorMessage);
      }

      return errorMessage;
    }

    hideErrorMessage() {
      const errorMessage = this.getErrorMessage();
      if (errorMessage) errorMessage.hidden = true;
    }

    showErrorMessage() {
      const errorMessage = this.getErrorMessage();
      if (errorMessage) errorMessage.hidden = false;
    }

    getNextPageUrl() {
      const nextUrl = new URL(this.nextButton.href, window.location.origin);
      const currentUrl = new URL(window.location.href);
      const shopifyTheme = window.Shopify?.theme;
      const previewThemeId = currentUrl.searchParams.get('preview_theme_id') || shopifyTheme?.id;
      const isPreviewTheme = currentUrl.searchParams.has('preview_theme_id') || (shopifyTheme?.role && shopifyTheme.role !== 'main');

      if (previewThemeId && isPreviewTheme) {
        nextUrl.searchParams.set('preview_theme_id', String(previewThemeId));
      }

      ['_fd', 'pb'].forEach((param) => {
        const value = currentUrl.searchParams.get(param);
        if (value !== null) {
          nextUrl.searchParams.set(param, value);
        }
      });

      // Use the collection section only; retain the existing full-page path for blogs/search.
      const sectionId = this.closest('.section-main-collection-product-grid')?.querySelector('#product-grid')?.dataset.id;
      if (sectionId) nextUrl.searchParams.set('section_id', sectionId);
      return nextUrl.toString();
    }

    normalizeProductBadges() {
      // Product badge layout is CSS-only so newly appended cards inherit it immediately.
    }

    // Sets up the infinite scroll functionality
    bindListeners() {
      // Setup click handler for the "Next" button
      this.clickHandler = this.onNextButtonClick.bind(this);
      this.nextButton.addEventListener('click', this.clickHandler);

      // Initialize IntersectionObserver if infinite scrolling is enabled
      if (this.dataset.infiniteScrollEnabled === 'true') {
        this.intersectionObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) this.fetchNextItems();
          });
        }, {rootMargin: `${window.innerHeight}px 0px`});

        this.intersectionObserver.observe(this.paginationContainer);
      }
    }

    // Fetches the next set of items and updates the DOM
    async fetchNextItems() {
      if (this.fetchInProgress) return;

      let originalButtonText = this.nextButton.textContent;
      this.hideErrorMessage();
      this.fetchInProgress = true;
      this.nextButton.setAttribute('aria-disabled', 'true');
      this.nextButton.textContent = window.strings.general.pagination.loadingText;

      try {
        const response = await fetch(this.getNextPageUrl());
        if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);

        const template = document.createElement('template');
        template.innerHTML = await response.text();
        if (!this.isConnected) return;

        // Append new items
        const newItems = Array.from(template.content.querySelectorAll('.js-pagination__item'));
        const fragment = document.createDocumentFragment();
        newItems.forEach((item) => fragment.appendChild(item));
        this.paginationItems[this.paginationItems.length - 1].after(fragment);
        const lastIndex = this.paginationItems.length;
        this.paginationItems = this.querySelectorAll('.js-pagination__item');
        const appendedItems = Array.from(this.paginationItems).slice(lastIndex);
        this.normalizeProductBadges(appendedItems);

        document.dispatchEvent(new CustomEvent('collection:products-updated', {
          detail: {
            reason: 'infinite-scroll',
            root: this,
            items: appendedItems
          }
        }));

        const enhanceNewItems = () => {
          if (!this.isConnected) return;
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
          window.renderSelects();
          window.loadDesktopOnlyTemplates();
          window.stickersReinit?.();
          this.normalizeProductBadges(appendedItems);

          if (document.body.classList.contains('tab-active') && window.theme?.settings.paginationLoadType === 'show_more') {
            this.paginationItems[lastIndex]?.querySelector('a')?.focus();
          }
        };

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(enhanceNewItems, { timeout: 1000 });
        } else {
          window.setTimeout(enhanceNewItems, 300);
        }

        // Update pagination controls
        this.updatePaginationControls(template);
      } catch (error) {
        console.error('Failed to load new items:', error);
        this.showErrorMessage();
      } finally {
        this.fetchInProgress = false;
        this.nextButton.removeAttribute('aria-disabled');
        this.nextButton.textContent = originalButtonText;
      }
    }

    // Updates pagination controls based on the fetched content
    updatePaginationControls(template) {
      const nextPagination = template.content.querySelector('.js-pagination');
      if (nextPagination && nextPagination.querySelector('.js-pagination__next-button[href]')) {
        this.paginationContainer.innerHTML = nextPagination.innerHTML;
        this.refreshNextButton();
      } else {
        this.cleanupAfterLastPage();
      }
    }

    // Refreshes the "Next" button event listener after content update
    refreshNextButton() {
      this.nextButton.removeEventListener('click', this.clickHandler);
      this.nextButton = this.querySelector('.js-pagination__next-button');
      this.nextButton.addEventListener('click', this.clickHandler);
    }

    // Removes pagination and related elements when no more pages are available
    cleanupAfterLastPage() {
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      this.paginationContainer.remove();
      document.querySelectorAll('.js-when-paginated-only').forEach(elem => elem.remove());
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}

